import { useContext, useEffect, useState } from 'react';
import { Button, SvgIcon } from '@mui/material';
import { useTranslation } from 'react-i18next';
import { Plus } from '../../../assets';
import { UserContext } from '../../../components/shared/useUser';
import { useLocation, useNavigate } from 'react-router-dom';
import { ListPageTemplate } from '../../../components/page-templates/list-page/ListPageTemplate';
import BackButton from '../../../components/shared/actions/BackButton';
import CategoriesListPageContent from './components/CategoriesListPageContent';
import { useInstanceMarket } from '../../../contexts/InstanceMarketContext';

const CategoriesPage = () => {
  const { t } = useTranslation('categories');
  const navigate = useNavigate();
  const location = useLocation();

  const { hasPermissionTo } = useContext(UserContext);
  const { defaultInstanceId, defaultFiltersLoading } = useInstanceMarket();
  const [hasPermissionToCreateCategory, setHasPermissionToCreateCategory] = useState<boolean>(false);

  const [currentFilterInstance, setCurrentFilterInstance] = useState<string>('');
  const [currentFilterSearchTerm, setCurrentFilterSearchTerm] = useState<string>('');

  useEffect(() => {
    const searchParams = new URLSearchParams(location.search);
    if (!searchParams.get('companyId') && !defaultFiltersLoading && defaultInstanceId) {
      setCurrentFilterInstance(defaultInstanceId);
      const params = new URLSearchParams();
      params.set('companyId', defaultInstanceId);
      navigate(`?${params.toString()}`, { replace: true });
    }
  }, [defaultFiltersLoading, defaultInstanceId]);

  useEffect(() => {
    setHasPermissionToCreateCategory(hasPermissionTo(['CategoryCreate', 'ConfigurationAdmin']));

    const searchParams = new URLSearchParams(location.search);
    const companyIdParam = searchParams.get('companyId');
    const searchTermParam = searchParams.get('searchTerm');

    setCurrentFilterInstance(companyIdParam || '');
    setCurrentFilterSearchTerm(searchTermParam || '');
  }, [hasPermissionTo, location.search]);

  const canViewCategories = hasPermissionTo(['CategoryView', 'ConfigurationAdmin']);

  if (!canViewCategories) {
    return null;
  }

  const handleCreateNew = () => {
    navigate('/procurement/categories/create');
  };

  const handleFilterChange = (instance: string, searchTerm?: string) => {
    setCurrentFilterInstance(instance);
    const newSearchTerm = searchTerm !== undefined ? searchTerm : currentFilterSearchTerm;
    setCurrentFilterSearchTerm(newSearchTerm);

    const params = new URLSearchParams();
    if (instance) params.set('companyId', instance);
    if (newSearchTerm) params.set('searchTerm', newSearchTerm);
    navigate(`?${params.toString()}`, { replace: true });
  };

  return (
    <CategoriesListPageContent
      onCreateNew={handleCreateNew}
      onFilterChange={handleFilterChange}
      initialInstance={currentFilterInstance}
      initialSearchTerm={currentFilterSearchTerm}
    >
      {({ filterBar, headlineContent, table, isInitialLoading }) => (
        <ListPageTemplate
          title={t('procurementPageTitle', 'Procurement Categories')}
          subtitle={t('procurementListSubtitle', 'Manage procurement categories, hierarchy, and icons.')}
          loading={isInitialLoading}
          loadingLabel={t('loading')}
          backButton={(
            <BackButton
              handleClick={() => navigate('/settings')}
              title={t('backToSettings', 'Back to Settings')}
              isLink={false}
            />
          )}
          actions={hasPermissionToCreateCategory && (
            <Button
              variant="primary"
              size="lg"
              startIcon={<SvgIcon><Plus fill='white' /></SvgIcon>}
              onClick={handleCreateNew}
            >
              {t('procurementCreateNew', 'Create New Procurement Category')}
            </Button>
          )}
          headlineContent={headlineContent}
          filterContent={filterBar}
          table={table}
        />
      )}
    </CategoriesListPageContent>
  );
};

export default CategoriesPage;
